/**
 * Macro #21 §7 — Purchase reload/recovery (production wiring).
 *
 * A page reload during a pending/indexing purchase must NOT lose an in-flight
 * transaction, and must NEVER:
 *   - send another transaction / re-open the wallet automatically,
 *   - invent optimistic ownership,
 *   - persist any secret.
 *
 * This module owns ONLY the minimal, non-secret recovery context and its
 * fail-closed validation. The blockchain + indexer remain the sole ownership
 * authority: recovery merely RESUMES tracking of an already-submitted tx.
 *
 * Recovery is keyed/validated against wallet + chainId + SaleV2 + txHash.
 * Geometry is CONTEXT ONLY (identity verification against the indexed record),
 * never an ownership authority.
 */

export const RECOVERY_STORAGE_KEY = "grid:purchase:recovery:v1";

/** A recovery context older than this is discarded fail-closed on load. */
export const RECOVERY_TTL_MS = 30 * 60 * 1000; // 30 minutes

export interface RecoveryGeometry {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PurchaseRecoveryContext {
  /** Normalized (lowercase) buyer wallet. */
  wallet: string;
  chainId: number;
  /** Normalized (lowercase) SaleV2 contract address. */
  saleAddr: string;
  /** Submitted transaction hash (0x + 64 hex). */
  txHash: string;
  /** Selected geometry — CONTEXT ONLY (verification), never authority. */
  geometry: RecoveryGeometry;
  /** Epoch millis at which the tx was submitted (TTL basis). */
  createdAtMs: number;
}

export interface RuntimeIdentity {
  wallet: string;
  chainId: number;
  saleAddr: string;
}

/** Minimal injectable storage (window.localStorage in production, a stub in tests). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type ReceiptStatus = "pending" | "success" | "reverted";
export type RecoveryPhase = "confirming" | "syncing" | "owned" | "failed" | "degraded";

export type RecoveryInvalidReason =
  | "absent"
  | "malformed"
  | "expired"
  | "wrong_wallet"
  | "wrong_chain"
  | "wrong_sale";

export type RecoveryLoad =
  | { ok: true; ctx: PurchaseRecoveryContext }
  | { ok: false; reason: RecoveryInvalidReason };

const _TX_HASH_RE = /^0x[0-9a-fA-F]{64}$/;

function _normAddr(a: unknown): string {
  return typeof a === "string" ? a.trim().toLowerCase() : "";
}

function _isFiniteInt(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v) && Math.floor(v) === v;
}

function _isValidGeometry(g: unknown): g is RecoveryGeometry {
  if (g == null || typeof g !== "object") return false;
  const o = g as Record<string, unknown>;
  return (
    _isFiniteInt(o.x) &&
    _isFiniteInt(o.y) &&
    _isFiniteInt(o.w) &&
    _isFiniteInt(o.h) &&
    (o.w as number) > 0 &&
    (o.h as number) > 0
  );
}

/**
 * Explicit allow-list serialization — guarantees NO secret and no stray field
 * can ever be written. Only the six recovery fields are stored.
 */
export function serializeRecovery(ctx: PurchaseRecoveryContext): string {
  const safe = {
    wallet: _normAddr(ctx.wallet),
    chainId: ctx.chainId,
    saleAddr: _normAddr(ctx.saleAddr),
    txHash: ctx.txHash,
    geometry: {
      x: ctx.geometry.x,
      y: ctx.geometry.y,
      w: ctx.geometry.w,
      h: ctx.geometry.h,
    },
    createdAtMs: ctx.createdAtMs,
  };
  return JSON.stringify(safe);
}

/** Parse + structurally validate. Malformed / unknown shape → null (never throws). */
export function parseRecovery(raw: string | null): PurchaseRecoveryContext | null {
  if (raw == null) return null;
  let obj: unknown;
  try {
    obj = JSON.parse(raw);
  } catch {
    return null;
  }
  if (obj == null || typeof obj !== "object") return null;
  const o = obj as Record<string, unknown>;
  const wallet = _normAddr(o.wallet);
  const saleAddr = _normAddr(o.saleAddr);
  if (!wallet || !saleAddr) return null;
  if (!_isFiniteInt(o.chainId)) return null;
  if (typeof o.txHash !== "string" || !_TX_HASH_RE.test(o.txHash)) return null;
  if (!_isValidGeometry(o.geometry)) return null;
  if (!_isFiniteInt(o.createdAtMs)) return null;
  const g = o.geometry as RecoveryGeometry;
  return {
    wallet,
    chainId: o.chainId as number,
    saleAddr,
    txHash: o.txHash,
    geometry: { x: g.x, y: g.y, w: g.w, h: g.h },
    createdAtMs: o.createdAtMs as number,
  };
}

export function persistRecovery(storage: StorageLike, ctx: PurchaseRecoveryContext): void {
  try {
    storage.setItem(RECOVERY_STORAGE_KEY, serializeRecovery(ctx));
  } catch {
    /* storage unavailable → recovery is a best-effort convenience, never a
       correctness dependency; the on-chain tx is unaffected. */
  }
}

export function clearRecovery(storage: StorageLike): void {
  try {
    storage.removeItem(RECOVERY_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Fail-closed validation of a candidate context against the CURRENT runtime
 * identity. Any mismatch (wallet/chain/sale), expiry, or malformed shape is
 * rejected — recovery never resumes blind.
 */
export function validateRecovery(
  ctx: PurchaseRecoveryContext | null,
  runtime: RuntimeIdentity,
  nowMs: number,
): RecoveryLoad {
  if (ctx == null) return { ok: false, reason: "absent" };
  if (!_TX_HASH_RE.test(ctx.txHash) || !_isValidGeometry(ctx.geometry)) {
    return { ok: false, reason: "malformed" };
  }
  if (!_isFiniteInt(ctx.createdAtMs) || nowMs - ctx.createdAtMs > RECOVERY_TTL_MS) {
    return { ok: false, reason: "expired" };
  }
  if (_normAddr(ctx.wallet) !== _normAddr(runtime.wallet)) {
    return { ok: false, reason: "wrong_wallet" };
  }
  if (ctx.chainId !== runtime.chainId) {
    return { ok: false, reason: "wrong_chain" };
  }
  if (_normAddr(ctx.saleAddr) !== _normAddr(runtime.saleAddr)) {
    return { ok: false, reason: "wrong_sale" };
  }
  return { ok: true, ctx };
}

/**
 * Load + validate + AUTO-CLEANUP. On any invalid/stale/mismatched context the
 * stored entry is removed (explicit terminal cleanup) so recovery can never be
 * retried blindly on the next reload.
 */
export function loadValidRecovery(
  storage: StorageLike,
  runtime: RuntimeIdentity,
  nowMs: number,
): RecoveryLoad {
  const raw = (() => {
    try {
      return storage.getItem(RECOVERY_STORAGE_KEY);
    } catch {
      return null;
    }
  })();
  const parsed = parseRecovery(raw);
  const result = validateRecovery(parsed, runtime, nowMs);
  if (!result.ok && result.reason !== "absent") {
    clearRecovery(storage);
  }
  return result;
}

/**
 * Map an already-submitted tx's on-chain + indexer status to a truthful UX
 * phase. Ownership ("owned") is asserted ONLY once the canonical indexer rule
 * has succeeded — never optimistically from a confirmed receipt alone.
 */
export function deriveRecoveryPhase(
  receipt: ReceiptStatus,
  indexedCanonical: boolean,
): RecoveryPhase {
  if (receipt === "reverted") return "failed";
  if (receipt === "pending") return "confirming";
  // receipt === "success"
  return indexedCanonical ? "owned" : "syncing";
}

export function isTerminalPhase(phase: RecoveryPhase): boolean {
  return phase === "owned" || phase === "failed";
}

/**
 * §6 receipt-aware resume resolution. Combines the on-chain receipt probe with
 * the canonical indexer state into a single truthful outcome. Ownership is
 * asserted ONLY via the canonical indexer; a reverted receipt terminates as a
 * real FAILED state with cleanup; an unreadable receipt (RPC down) is a
 * retryable DEGRADED state — NEVER owned. Never triggers a new wallet write.
 */
export type ReceiptProbe =
  | { kind: "pending" }
  | { kind: "success" }
  | { kind: "reverted" }
  | { kind: "unavailable" }; // RPC failure / receipt not readable yet

export interface RecoveryOutcome {
  phase: RecoveryPhase;
  /** start/continue the canonical indexer poll for this txHash */
  trackIndexer: boolean;
  /** remove the persisted recovery context now (terminal) */
  cleanup: boolean;
  /** safe to re-probe the receipt later (non-terminal) */
  retry: boolean;
}

export function resolveRecoveryOutcome(
  receipt: ReceiptProbe,
  canonicalIndexed: boolean,
): RecoveryOutcome {
  // Canonical ownership is authoritative regardless of the receipt read state.
  if (canonicalIndexed) {
    return { phase: "owned", trackIndexer: false, cleanup: true, retry: false };
  }
  switch (receipt.kind) {
    case "unavailable":
      // Honest degraded state — retry the probe, never guess owned, keep ctx.
      return { phase: "degraded", trackIndexer: false, cleanup: false, retry: true };
    case "reverted":
      // Real terminal failure — the canonical record will NEVER arrive.
      return { phase: "failed", trackIndexer: false, cleanup: true, retry: false };
    case "pending":
      return { phase: "confirming", trackIndexer: true, cleanup: false, retry: true };
    case "success":
    default:
      return { phase: "syncing", trackIndexer: true, cleanup: false, retry: true };
  }
}
